"use client";

import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { PostSummary } from "@/lib/ressources";
import { PostCard } from "./PostCard";
import { CATEGORY_META } from "./category-meta";
import type { Category } from "./category-meta";
import { TagLabel } from "./TagLabel";
import { canonicalTag, orderedFilterTags } from "./topic-tags";
import { useListingScroll, useListingState } from "./nav-history";
import Link from "next/link";
import { FaArrowLeft } from "react-icons/fa6";

/** Shared look of the filter chips; 40 px tall on phones (touch), 36 px from `sm`. */
const CHIP =
  "inline-flex min-h-10 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors sm:min-h-9 sm:px-3.5";

/**
 * A category page of the Resources: back link, title and description, topic filters and
 * the article grid.
 *
 * The filters are toggle buttons (`aria-pressed`) with readable labels, deduplicated
 * (see topic-tags.ts); on phones they sit on one row that scrolls sideways instead of
 * stacking into a wall of chips. The number of matching articles is announced politely
 * after each change.
 */
export function CategoryIndexClient({
  posts,
  tags,
  category,
  locale = "fr",
}: {
  posts: PostSummary[];
  tags: string[];
  category: Category;
  locale?: "fr" | "en";
}) {
  const intl = useIntl();
  const [activeTag, setActiveTag] = useListingState<string | null>("tag", null);
  useListingScroll();
  const meta = CATEGORY_META[category];
  const langSuffix = locale === "en" ? "/en" : "";
  const filterTags = useMemo(() => orderedFilterTags(tags, locale), [tags, locale]);

  const filtered = useMemo(
    () => (activeTag ? posts.filter((p) => p.tags.some((t) => canonicalTag(t) === activeTag)) : posts),
    [posts, activeTag]
  );

  return (
    <div>
      <Link
        href={`/resources${langSuffix}`}
        className="-mt-3 mb-5 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-brand hover:text-brand-ink"
      >
        <FaArrowLeft className="text-xs" aria-hidden="true" />
        <FormattedMessage id="ressources.backToList" />
      </Link>

      <div className="mb-2 flex items-center gap-3">
        <span aria-hidden="true" className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${meta.color} ${meta.bg}`}>
          <FormattedMessage id={meta.labelKey} />
        </span>
      </div>
      <h1 className="mb-3 text-3xl font-bold text-text-main sm:text-4xl 2xl:text-5xl">
        <FormattedMessage id={meta.labelKey} />
      </h1>
      <p className="mb-10 max-w-2xl text-gray-main 2xl:text-lg">
        <FormattedMessage id={meta.descKey} />
      </p>

      {filterTags.length > 0 && (
        <div
          role="group"
          aria-label={intl.formatMessage({ id: "ressources.tags.label" })}
          // Keeps a chip reached with Tab fully inside the sideways-scrolling row.
          onFocus={(e) => e.target.scrollIntoView({ block: "nearest", inline: "nearest" })}
          className="-mx-6 mb-8 flex gap-2 overflow-x-auto scroll-px-6 px-6 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
        >
          <button
            type="button"
            aria-pressed={activeTag === null}
            onClick={() => setActiveTag(null)}
            className={`${CHIP} ${activeTag === null ? "bg-blue-main text-white" : "bg-gray-ulight text-text-main hover:bg-gray-light"}`}
          >
            <FormattedMessage id="ressources.tags.all" />
          </button>
          {filterTags.map((tag) => (
            <button
              type="button"
              key={tag}
              aria-pressed={activeTag === tag}
              onClick={() => setActiveTag(tag === activeTag ? null : tag)}
              className={`${CHIP} ${activeTag === tag ? "bg-blue-main text-white" : "bg-gray-ulight text-text-main hover:bg-gray-light"}`}
            >
              <TagLabel tag={tag} />
            </button>
          ))}
        </div>
      )}

      {/* Announces the result count after a filter change (silent on first render). */}
      <p aria-live="polite" className="sr-only">
        {activeTag !== null && <FormattedMessage id="ressources.results" values={{ count: filtered.length }} />}
      </p>

      {filtered.length === 0 ? (
        <p className="py-16 text-center text-gray-main">
          <FormattedMessage id="ressources.empty.title" />
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:gap-8">
          {filtered.map((post, k) => (
            <PostCard key={post.slug} post={post} locale={locale} headingLevel="h2" priority={k === 0} />
          ))}
        </div>
      )}
    </div>
  );
}
